import moment from 'moment';

export interface FinancialPeriod {
  from: string;
  to: string;
}

export interface FinancialProtection {
  periods: FinancialPeriod[];
  months: FinancialPeriod[];
}

export const FINANCIAL_PROTECTION_UNAVAILABLE = 'We couldn’t check whether these records can be changed. Changes are temporarily unavailable. Refresh this view to check again.';

export function financialProtectionAvailable(protection: FinancialProtection | undefined): protection is FinancialProtection {
  return !!protection && Array.isArray(protection.periods) && Array.isArray(protection.months);
}

export function financialProtectionMessage(protection: FinancialProtection | undefined, monthly = true, message?: (period: string) => string): string {
  if (!protection || !Array.isArray(protection.periods) || !Array.isArray(protection.months)) {
    return FINANCIAL_PROTECTION_UNAVAILABLE;
  }
  const periods = monthly ? protection.months : protection.periods;
  if (!periods.length) return '';
  const period = periods.map(p => `${p.from} – ${p.to}`).join(', ');
  return message ? message(period) : `Financial records protected (Ready to Pay or Locked): ${period}. ${monthly ? 'These complete calendar months are protected because financial reports also use monthly records. ' : ''}Keep these records unchanged; arrange corrections in a later open period with Finance.`;
}

// Only known financial refusals have the guarantees described by this copy.
export function financialProtectionErrorMessage(
  response: { status?: number; error?: { code?: string; msg?: string; periods?: FinancialPeriod[] } },
  message?: (period: string) => string,
  assignment: 'single' | 'bulk' | 'approve' | 'import' = 'single',
): string {
  if (response?.status !== 423 || response.error?.code !== 'FINANCIAL_REPORT_DATA_LOCKED') return '';
  if (response.error.msg === 'This phase ends before the employee can be assigned after the protected financial period.') {
    // This error's periods describe the attempted assignment, not protected records.
    if (assignment === 'import') return 'Import blocked: an employee has no available work dates in the phase after the protected period. No changes from this import were applied. Review the planned assignment; contact Finance if protected records need correcting.';
    if (assignment === 'bulk' || assignment === 'approve') {
      return `${assignment === 'approve' ? 'None of the selected recommendations were approved.' : 'None of the selected employees were assigned to the phase.'} At least one has no available work dates after the protected period. Review the selection or choose a later phase; contact Finance if protected records need correcting.`;
    }
    return 'This employee can’t be assigned to this phase. No available work dates remain after the protected period. Choose a later phase, or contact Finance if the protected records need correcting.';
  }
  if (response.error.msg !== 'This period is protected by a financial report. Make the correction in an open period.') return '';
  const period = Array.isArray(response.error.periods)
    ? response.error.periods.filter(p => p?.from && p?.to).map(p => `${p.from} – ${p.to}`).join(', ')
    : '';
  if (assignment === 'import') return `Import blocked by a financial report. The file would change financial records protected ${period ? `for ${period}` : 'by a financial report'}. No changes from this import were applied. Correct the file if it contains a mistake, or contact Finance if the protected records need updating.`;
  return message ? message(period || 'the protected period') : 'Your change wasn’t saved. These records are protected by a financial report. Keep them unchanged; contact Finance about a correction in a later open period. Refresh this view to check the current restrictions.';
}

export function protectedRange(protection: FinancialProtection | undefined, from: string, to: string = from, monthly = true): boolean {
  if (!protection || !Array.isArray(protection.periods) || !Array.isArray(protection.months)) return true;
  return (monthly ? protection.months : protection.periods).some(p => (from || '0001-01-01') <= p.to && (to || '9999-12-31') >= p.from);
}

// Compare the part of an assignment/contract that belongs to each closed period.
export function protectedRangeChanged(protection: FinancialProtection | undefined, oldFrom: string, oldTo: string, newFrom: string, newTo: string, valuesChanged = false, monthly = true): boolean {
  if (!protection || !Array.isArray(protection.periods) || !Array.isArray(protection.months)) return true;
  return (monthly ? protection.months : protection.periods).some(p => {
    const clip = (from: string, to: string) => {
      const start = (from || '0001-01-01') > p.from ? from : p.from;
      const end = (to || '9999-12-31') < p.to ? to : p.to;
      return start <= end ? `${start}/${end}` : '';
    };
    const before = clip(oldFrom, oldTo);
    const after = clip(newFrom, newTo);
    return before !== after || (!!before && valuesChanged);
  });
}

export function financialDate(value: unknown): string | null {
  return value && moment(value as string).isValid() ? moment(value as string).format('YYYY-MM-DD') : null;
}

// Moving the latest wage also extends or shortens the preceding wage.
export function protectedWageChange(protection: FinancialProtection | undefined, oldDate: string, newDate: string, oldWage: number | null, newWage: number | null, previousWage: number | null): boolean {
  if (!oldDate || !newDate) return protectedRangeChanged(protection, oldDate, null, newDate, null, oldWage !== newWage, false);
  const laterDate = oldDate > newDate ? oldDate : newDate;
  if (oldWage !== newWage && protectedRange(protection, laterDate, null, false)) return true;
  if (oldDate === newDate) return false;
  const movedRate = oldDate < newDate ? oldWage : newWage;
  return movedRate !== previousWage && protectedRange(
    protection,
    oldDate < newDate ? oldDate : newDate,
    moment(laterDate).subtract(1, 'day').format('YYYY-MM-DD'),
    false,
  );
}
